<template>
  <div class="create-form-container" ref="createFormContainer">
    <ContentContainerNavbar :title="formIdProp ? $t('i18n.editFormDesignNavbarTitle') : $t('i18n.createFormDesignNavbarTitle')" />
    <v-form @submit.prevent="submitForm" ref="form">
      <div class="form-card">
        <div class="form-configuration-sidebar">
          <div v-if="formIdProp" class="sidebar-title text-h5 font-weight-black">{{ $t('i18n.editForm') }}</div>
          <div v-else class="sidebar-title text-h5 font-weight-black">{{ $t('i18n.createForm') }}</div>
          <div class="form-name font-weight-black pb-2">{{ $t('i18n.formName') }}</div>

          <v-text-field density="compact" variant="outlined" dense hide-details="auto" v-model="formObject.form.name" clearable :rules="nameRules"></v-text-field>
          <div class="form-description font-weight-black pb-2 pt-3">{{ $t('i18n.description') }}</div>
          <v-text-field class="form-description-text-field" density="compact" variant="outlined" dense hide-details v-model="formObject.form.description" clearable></v-text-field>

          <!-- Aqui são exibidos os form items que o user pode arrastar para o sketch/esboço -->
          <div class="form-items-container pt-3">
            <div class="form-items-container-title font-weight-black pb-2">{{ $t('i18n.formItems') }}</div>
            <div class="items">
              <draggable v-if="getFormItemsEditStatus" :list="availableFormItemsList" :group="{ name: 'form_items', pull: 'clone', put: false }" :sort="false" item-key="id">
                <template #item="{ element, index }">
                  <FormItemCard :name="element.name" :iconName="element.icon" @click="addFormItem(index)" clickable draggable />
                </template>
              </draggable>
              <div v-else class="cursor-not-allowed" v-for="(element, index) in availableFormItemsList" :key=index>
                <FormItemCard :name="element.name" :iconName="element.icon" />
              </div>
            </div>
          </div>
          <!----------------------------------------------------------------------------------->

          <v-divider :thickness="2" class="divider border-opacity-100 mt-3" color="black"></v-divider>

          <div class="switch-options-container">
            <div class="font-weight-black">{{ $t('i18n.bilingual') }}</div>
            <v-switch size="x-small" class="form-option-btn" v-model="bilingual" hide-details focused color="#ffd139" ></v-switch>
            <div class="font-weight-black" style="width: 100%;">{{ $t('i18n.itemPerPage') }}</div>
            <v-switch size="small" class="form-option-btn" v-model="formObject.form.layout_mode" hide-details focused color="#ffd139" :value="'one_per_page'" :false-value="'all_on_one_page'"></v-switch>
          </div>
          <v-divider :thickness="2" class="divider border-opacity-100 mb-3" color="black"></v-divider>
          <div class="associate-device font-weight-black pb-2">{{ $t('i18n.associateDevice') }}</div>
          <v-select v-model="latestAssociatedDevice" :items=availableDevicesList :placeholder="$t('i18n.deviceListPlaceholder')" variant="outlined" density="compact" required return-object item-title="name" item-value="id" :no-data-text="$t('i18n.noDevicesAvailable')">
          </v-select>
          <div class="associated-devices-chip-container">
            <v-skeleton-loader class="skeleton-loader" v-if="loadingDevices" type="card"></v-skeleton-loader>
            <v-chip class="gorate-bg mr-2 mb-2" v-for="device in devicesAssociatedToForm" :key="device.id" label closable @click:close="removeAssociatedDevice(device.id)">
              {{ device.name }}
            </v-chip>
          </div>
        </div>
        <div class="form-sketch-container">
          <div class="form-title font-weight-black">
            {{ formObject.form.name }} {{ $t('i18n.sketch') }} <span v-if="displayedFormItemsList.length > 0">({{
              displayedFormItemsList.length }} {{ $t('i18n.items') }})</span>
          </div>
          <div class="sketch">
            <draggable class="draggable-container" :list="displayedFormItemsList" :sort="true" :group="{ name: 'form_items' }" item-key="uuid" @add="handleDragToAdd" @change="handleItemChangedisplayedFormItemsList">
              <template #item="{ element, index }">
                <div>
                  <!-- ATENÇÃO: Só o NavigationItem usa a prop formList -->
                  <component :is="element.component_name" :indexInTheSketchForm="index" :bilingual="bilingual" :formItems="getFormItemsIfEditing" :userCanEditFormItems="getFormItemsEditStatus" :formList="formList" @remove-item="removeItemFromSketch" @duplicate-item="duplicateItemInSketch" @form-item-update="updateFormData">
                  </component>
                </div>
              </template>
            </draggable>
          </div>
          <!-- Botões de cancel, preview e save -->
          <div class="sketch-footer">
            <v-divider :thickness="2" class="border-opacity-100 mb-3" color="black"></v-divider>
            <div class="sketch-footer-buttons">
              <v-btn class="ml-2 gorate-btn-bg-outlined" @click="handleCancelFormDesignButtonClick" variant="flat">{{ $t('i18n.cancel') }}</v-btn>
              <v-btn class="ml-2 gorate-btn-bg-outlined" @click="formPreviewIsActive = true" variant="flat">{{ $t('i18n.preview') }}</v-btn>
              <!-- <v-btn class="ml-2" variant="outlined">Preview</v-btn> -->
              <v-btn v-if="formIdProp" class="ml-2 gorate-btn-bg" type="submit" variant="flat">{{ $t('i18n.submit') }}</v-btn>
              <v-btn v-else class="ml-2 gorate-btn-bg" type="submit" variant="flat">{{ $t('i18n.create') }}</v-btn>
            </div>
          </div>
        </div>
      </div>
    </v-form>
  </div>
  <!--
   Dialogs are below
  -->
  <v-dialog v-model="quitFormDesignModalIsActive" max-width="450" @afterLeave="isCancelFormDesign = false">
    <QuitFormDesignModal :modalTitle="getQuitFormDesignDialogTitle" :modalMessage="getQuitFormDesignDialogMessage" @close-modal="cancelQuitNavigation" @confirm-quit-navigation="confirmQuitNavigation" />
  </v-dialog>
  <v-dialog v-model="formPreviewIsActive" max-width="800">
    <FormPreview :formObject="formObject" :propBilingual="bilingual" @close-preview="closeFormPreview" />
  </v-dialog>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { useRouter, NavigationGuardNext } from 'vue-router'
import draggable from 'vuedraggable'
import cloneDeep from 'lodash/cloneDeep'

import { FormDeviceAssociation, FormItem, FormItemType, FormResponseCount, SimplifiedDevice, Device, HttpResponse } from '@/utils/DataTypes'

import ContentContainerNavbar from '../navbars/ContentContainerNavbar.vue'

import FormItemCard from '../cards/FormItemCard.vue'

import TextLabel from '../form_items/TextLabel.vue'
import TextInput from '../form_items/TextInput.vue'
import ChoiceInput from '../form_items/ChoiceInput.vue'
import RatingInput from '../form_items/RatingInput.vue'
import NavigationItem from '../form_items/NavigationItem.vue'

import QuitFormDesignModal from '../modals/form_modals/QuitFormDesignModal.vue'
import FormPreview from '@/components/modals/FormPreview.vue'

import { VForm } from 'vuetify/lib/components/index.mjs'
import { api, getRandomUid } from '@/utils/HelperFunctions'
import { useAppStore } from '@/stores/appStore'

export default defineComponent({
  name: 'DesignForm',
  setup() {
    const router = useRouter()
    const appStore = useAppStore()
    return {
      router,
      appStore
    }
  },
  components: {
    draggable,
    ContentContainerNavbar,
    FormItemCard,
    TextLabel,
    TextInput,
    ChoiceInput,
    RatingInput,
    NavigationItem,
    QuitFormDesignModal,
    FormPreview
  },
  props: {
    formIdProp: {
      type: String,
      required: false,
      default: null
    }
  },
  async mounted() {
    this.availableFormItemsList = this.appStore.getAppState.formItemTypesList
    await this.getFormList()
    await this.getDeviceList()
    if (this.formIdProp) {
      await this.getFormResponseCount()
      await this.getFormToEdit()
    }
    this.loadingDevices = false
  },
  data() {
    return {
      formResponseCount: '0',
      formPreviewIsActive: false,
      formIsValid: false as boolean | undefined,
      bilingual: false,
      itemPerPage: false,
      loadingDevices: true,

      quitFormDesignModalIsActive: false,
      isCancelFormDesign: false, // Se carregar no botão de "cancel", a mensagem do dialog é uma (deseja mesmo cancelar ?), se carregar num dos tabs da navbar a mensagem é outra (deseja mesmo sair?)

      pendingNavigation: null as NavigationGuardNext | null,

      formList: [] as FormDeviceAssociation[],
      availableDevicesList: [] as Device[],
      devicesAssociatedToForm: [] as Device[] | SimplifiedDevice[],
      displayedFormItemsList: [] as FormItemType[],
      availableFormItemsList: [] as FormItemType[],

      formObject: {
        associatedDeviceIDs: [] as number[],
        form: {
          name: this.$t('i18n.defaultFormName'),
          description: '',
          layout_mode: 'all_on_one_page'
        },
        form_items: [] as FormItem[]
      },

      latestAssociatedDevice: null as Device | null
    }
  },
  watch: {
    latestAssociatedDevice: {
      handler(deviceObject) {
        if (deviceObject && !this.devicesAssociatedToForm.includes(deviceObject)) {
          this.formObject.associatedDeviceIDs.push(deviceObject.id)
          this.devicesAssociatedToForm.push(deviceObject)
          this.latestAssociatedDevice = null
        }
      }
    }
  },
  beforeRouteLeave(to, from, next) {
    if (to.name === 'Login') next() // Mudança de rota devido ao token ter expirado
    if (!this.formIsValid) {
      if (this.quitFormDesignModalIsActive) {
        next()
      } else {
        this.pendingNavigation = next
        this.quitFormDesignModalIsActive = true
      }
    } else { // Form validado e submetido, é suposto deixar sair
      next()
    }
  },
  methods: {
    async getFormResponseCount() {
      try {
        const url = `/forms/response-count/${this.formIdProp}`
        const getFormResponseCount = await api(url, 'get')

        if (!getFormResponseCount) return

        const getFormResponseCountData = getFormResponseCount.data as Array<FormResponseCount>
        this.formResponseCount = getFormResponseCountData[0].response_count
      } catch (error) {
        this.appStore.addAlert('error', 'Couldn\'t get form response count.')
      }
    },
    async removeAssociatedDevice(deviceID: number | undefined) {
      if (deviceID !== undefined) {
        const index = this.devicesAssociatedToForm.findIndex(device => device.id === deviceID)
        if (index !== -1) {
          this.devicesAssociatedToForm.splice(index, 1)

          const idIndex = this.formObject.associatedDeviceIDs.indexOf(deviceID)
          if (idIndex !== -1) {
            this.formObject.associatedDeviceIDs.splice(idIndex, 1)
          }
        }
      }
    },
    async getFormToEdit() {
      try {
        const url = `/forms/${this.formIdProp}`
        const getFormResponse = await api(url, 'get')

        if (!getFormResponse) return

        const form = getFormResponse?.data?.[0]

        this.formObject.form.name = form.name
        this.formObject.form.description = form.description
        this.formObject.form.layout_mode = form.layout_mode
        this.formObject.form_items = form.form_items

        const currentSimplifiedForm = this.formList.find((simplifiedForm: FormDeviceAssociation) => simplifiedForm.id === parseInt(this.formIdProp))
        if (currentSimplifiedForm) {
          this.devicesAssociatedToForm = currentSimplifiedForm.associated_devices
        } else {
          this.devicesAssociatedToForm = []
          this.appStore.addAlert('error', 'Couldn\'t get list of devices associated to form.')
        }
        this.formObject.associatedDeviceIDs = currentSimplifiedForm ? currentSimplifiedForm.associated_devices.map((device: SimplifiedDevice) => device.id) : []

        this.formObject.form_items.forEach(formItem => {
          this.bilingual = formItem.bilingual

          // Find the matching FormItemType based on item_type_id
          const matchingItemType = this.availableFormItemsList.find(
            itemType => itemType.id === formItem.item_type_id
          )

          // If a match is found, push the corresponding itemType into the displayedFormItemsList
          if (matchingItemType) {
            const displayedFormItem = cloneDeep(matchingItemType)
            displayedFormItem.uuid = getRandomUid()
            this.displayedFormItemsList.push(displayedFormItem)
          }
        })
      } catch (error) {
        this.appStore.addAlert('error', 'Error getting form to edit.')
      }
    },
    async validateForm() {
      const form = this.$refs.form as VForm
      try {
        const { valid } = await form.validate()
        if (this.formObject.form_items.length === 0) {
          this.appStore.addAlert('error', this.$t('i18n.formItemsMinLength'))
          return false
        }
        if (this.formObject.form_items.length > 10) {
          this.appStore.addAlert('error', this.$t('i18n.formItemsMaxLength'))
          return false
        }
        if (this.hasInvalidMultiChoiceItems) {
          this.appStore.addAlert('error', this.$t('i18n.formMultiChoiceOptions'))
          return false
        }
        if (this.hasInvalidNavigationItems) {
          this.appStore.addAlert('error', this.$t('i18n.formNavigationOptions'))
          return false
        }
        return valid
      } catch (error) {
        console.error('Validation Error: ', error)
        this.appStore.addAlert('error', 'Validation error. Please check input fields.')
      }
    },
    async getFormList() {
      try {
        const url = '/forms-devices-association'
        const getFormDeviceAssociationResponse = await api(url, 'get')

        if (!getFormDeviceAssociationResponse) return

        this.formList = getFormDeviceAssociationResponse.data
      } catch (error) {
        this.appStore.addAlert('error', 'Couldn\'t get form list.')
      }
    },
    async getDeviceList() {
      try {
        this.loadingDevices = true
        const url = '/devices'
        const getDeviceListResponse = await api(url, 'get')

        if (!getDeviceListResponse) return

        const allDevices = getDeviceListResponse.data
        this.availableDevicesList = allDevices.filter((device: any) => device.form_id === null)
      } catch (error) {
        this.appStore.addAlert('error', 'Couldn\'t get devices available to associate forms.')
      } finally {
        this.loadingDevices = false
      }
    },
    async submitForm() {
      this.formIsValid = await this.validateForm()
      if (!this.formIsValid) return

      try {
        const isEditing = !!parseInt(this.formIdProp)
        if (isEditing) {
          // Ordenação de form items
          this.formObject.form_items = this.formObject.form_items.map((item, index) => ({
            ...item,
            form_item_order: index + 1
          }))
        }

        const url = `/forms${isEditing ? `/${this.formIdProp}` : ''}`
        const method = isEditing ? 'put' : 'post'
        const submitFormResponse = await api(url, method, this.formObject)
        if (!submitFormResponse) return

        const { statusCode, responseMessage } = submitFormResponse.data

        if (statusCode === 200 || statusCode === 201) { // 200: UPDATE OK, 201: CREATE OK
          this.appStore.addAlert('success', responseMessage)
          this.navigate('Forms')
        } else {
          console.error('Error submitting form:', responseMessage)
          this.appStore.addAlert('error', responseMessage)
        }
      } catch (error) {
        console.error('Form submission error:', error)
        this.appStore.addAlert('error', 'Form submission failed. Please check input fields. Contact support if the error persists.')
      }
    },
    handleCancelFormDesignButtonClick() {
      this.isCancelFormDesign = true // Se carregar no botão de "cancel", a mensagem do dialog é uma (deseja mesmo cancelar ?)
      this.navigate('Forms')
    },
    navigate(routeName: string) {
      this.router.push({ name: routeName })
    },
    handleDragToAdd(event: any) {
      // Remove the item that Vue.Draggable automatically added automaticamente
      this.displayedFormItemsList.splice(event.newIndex, 1)

      // Inserir manualmente para poder atribuir uuid
      const newDisplayedFormItem = cloneDeep(this.availableFormItemsList[event.oldIndex])
      newDisplayedFormItem.uuid = getRandomUid()

      this.displayedFormItemsList.splice(event.newIndex, 0, newDisplayedFormItem)
    },
    handleItemChangedisplayedFormItemsList(event: any): void {
      if (event.moved) {
        const { oldIndex, newIndex } = event.moved
        const movedFormItem = cloneDeep(this.formObject.form_items.splice(oldIndex, 1)[0])
        this.formObject.form_items.splice(newIndex, 0, movedFormItem)
      }
    },
    updateFormData({ formItemIndex, data }: { formItemIndex: number; data: FormItem }) {
      if (formItemIndex >= this.formObject.form_items.length) {
        this.formObject.form_items.length = formItemIndex + 1
      }
      this.formObject.form_items[formItemIndex] = data
    },
    addFormItem(index: number) {
      const canEditForm = this.formIdProp ? this.getFormItemsEditStatus : true // Se for edição de formulário, só pode editar se não existirem respostas associadas
      if (canEditForm) {
        const newDisplayedFormItem = cloneDeep(this.availableFormItemsList[index])
        newDisplayedFormItem.uuid = getRandomUid()
        this.displayedFormItemsList.push(newDisplayedFormItem)
      }
    },
    removeItemFromSketch(index: number) {
      this.displayedFormItemsList.splice(index, 1)
      this.formObject.form_items.splice(index, 1)
    },
    duplicateItemInSketch(index: number) {
      const displayedFormItemObjectToDuplicate = cloneDeep(this.displayedFormItemsList[index])
      displayedFormItemObjectToDuplicate.uuid = getRandomUid()
      this.displayedFormItemsList.splice(index + 1, 0, displayedFormItemObjectToDuplicate)

      const formItemObjectToDuplicate = cloneDeep(this.formObject.form_items[index])

      if (formItemObjectToDuplicate === undefined) return

      const newFormItem: FormItem = {
        name: formItemObjectToDuplicate.name,
        item_type_id: formItemObjectToDuplicate.item_type_id,
        question_lang1: '',
        required: true,
        bilingual: this.bilingual
      }
      this.formObject.form_items.splice(index + 1, 0, newFormItem)
    },
    confirmQuitNavigation() {
      if (this.pendingNavigation) {
        this.pendingNavigation() // Continue navigation
        this.pendingNavigation = null
      }
      this.quitFormDesignModalIsActive = false
    },
    closeFormPreview() {
      this.formPreviewIsActive = false
    },
    cancelQuitNavigation() {
      this.pendingNavigation = null // Reset navigation guard
      this.quitFormDesignModalIsActive = false
      setTimeout(() => {
        this.isCancelFormDesign = false // Sem este timeout, a mensagem passa de "cancel" para "leave" enquanto o dialog ainda está a encerrar
      }, 500)
    },
    openQuitFormDesignModal() {
      this.quitFormDesignModalIsActive = true
    },
    resetScroll() {
      const createFormContainer = this.$refs.createFormContainer as HTMLElement
      if (createFormContainer) createFormContainer.scrollTop = 0
    }
  },
  computed: {
    nameRules(): ((value: string) => boolean | string)[] {
      return [
        (value: string) => {
          if (value) return true
          this.resetScroll()
          return this.$t('i18n.formNameRequired')
        }, (value: string): boolean | string => {
          if (value && value.length <= 30) return true
          this.resetScroll()
          return this.$t('i18n.formNameMaxLength')
        }, (value: string): boolean | string => {
          if (value && value.length >= 3) return true
          this.resetScroll()
          return this.$t('i18n.formNameMinLength')
        }, (value: string) => {
          const formNameAlreadyExists = this.formList.some(item => item.name.trim() === value.trim()) && !this.formIdProp
          if (value && formNameAlreadyExists) {
            this.resetScroll()
            return this.$t('i18n.formNameExists')
          }
          return true
        }
      ]
    },
    getFormItemsIfEditing(): FormItem[] | null {
      if (this.formIdProp) {
        return this.formObject.form_items
      }
      return null
    },
    getFormItemsEditStatus(): boolean {
      if (this.formIdProp) {
        return this.formResponseCount === '0'
      }
      return true
    },
    getQuitFormDesignDialogTitle(): string {
      if (this.isCancelFormDesign) {
        return this.$t('i18n.cancelFormDesignModalTitle')
      } else {
        return this.$t('i18n.leaveFormDesignModalTitle')
      }
    },
    getQuitFormDesignDialogMessage(): string {
      if (this.isCancelFormDesign) {
        return this.$t('i18n.cancelFormDesignModalConfirmationMessage')
      } else {
        return this.$t('i18n.leaveFormDesignModalConfirmationMessage')
      }
    },
    hasInvalidMultiChoiceItems(): boolean {
      return this.formObject.form_items.some((item: FormItem) => {
        if (item.name === 'MULTI_CHOICE') {
          const options = item.form_item_options
          return !options || options.length <= 1
        }
        return false
      })
    },
    hasInvalidNavigationItems(): boolean {
      return this.formObject.form_items.some((item: FormItem) => {
        if (item.name === 'NAVIGATION') {
          const options = item.form_item_options
          return !options || options.length <= 0
        }
        return false
      })
    }
  }
})
</script>

<style lang="scss" scoped>
@import "../../styles/colors.scss";

.create-form-container {
  height: 100%;
  display: grid;
  grid-template-rows: auto 1fr;
  overflow-y: auto;

  .form-card {
    height: 100%;
    display: grid;
    grid-template-columns: 20% 1fr;
    margin: 1.0em;
    border-radius: 15px;
    background-color: $white;

    .form-configuration-sidebar {
      grid-column-start: 1;
      grid-column-end: 2;
      align-items: start;

      padding: 1.0em 2.0em;
      background-color: $grey-3;
      border-radius: 15px 0px 0px 15px;
      box-shadow: 2px 0 25px 0 rgba(0, 0, 0, 0.27);

      .sidebar-title {
        padding: 0.5em 0em;
      }

      .form-items-container {
        display: grid;
        grid-template-rows: auto 1fr;
      }

      .switch-options-container {
        display: grid;
        grid-template-columns: 1fr auto;
        align-items: center;

        .form-option-btn {
          justify-self: end;
        }
      }

      .associated-devices-chip-container {}
    }

    .form-sketch-container {
      grid-column-start: 2;
      grid-column-end: 3;

      display: grid;
      grid-template-rows: auto 1fr auto;

      .form-title {
        margin-top: 1.0em;
        padding: 1.0em 2.0em;
      }

      .sketch {
        display: grid;
        grid-template-rows: 1fr;
        padding: 1.0em 2.0em;

        .draggable-container {
          height: 100%;
        }
      }

      .sketch-footer {
        display: grid;
        grid-template-rows: auto;
        padding: 1.0em 0em;

        .sketch-footer-buttons {
          display: flex;
          justify-content: flex-end;
          padding: 0em 2.0em;
          align-items: center;
        }
      }
    }
  }
}
</style>
